import React, { useEffect, useRef, useState } from "react"; // useRef kept for apiResult
import { startUpload, pollJob } from "../api";

const BASE_STEPS = [
  { id: "validate",  icon: "✅", label: "File Validation",                  desc: "Checking file format and integrity",                                    duration: 150  },
  { id: "ocr",       icon: "🔍", label: "OCR Text Extraction",              desc: "Tesseract OCR extracting text from document",                         duration: 800  },
  { id: "translate", icon: "🌐", label: "Language Detection & Translation", desc: "Auto-detecting language · Translating to English via Ollama",           duration: 600  },
  { id: "parse",     icon: "🔧", label: "Data Parsing & Cleaning",          desc: "Converting English text to structured format via Pandas",               duration: 500  },
  { id: "ml",        icon: "🤖", label: "ML Risk Prediction",               desc: "Scikit-learn models scoring malnutrition, anemia & growth risk",        duration: 900  },
  { id: "llm",       icon: "💡", label: "AI Insight Generation",            desc: null, /* filled dynamically from /config */                              duration: 1200 },
  { id: "dss",       icon: "🚨", label: "DSS Alert Evaluation",             desc: "Decision Support System checking risk thresholds",                      duration: 300  },
  { id: "done",      icon: "📊", label: "Results Ready",                    desc: "Dashboard prepared with full analytics in English",                     duration: 200  },
];

const STATUS = { PENDING: "pending", ACTIVE: "active", DONE: "done", SKIP: "skip" };
const POLL_INTERVAL_MS = 2000;

export default function PipelineStatus({ file, onDone, onError }) {
  const ext          = file?.name?.split(".").pop().toLowerCase();
  const isImageOrPDF = ["png", "jpg", "jpeg", "pdf"].includes(ext);

  // Cache model name in localStorage so it shows instantly on repeat visits
  const cachedModel  = localStorage.getItem("ollama_model") || "";
  const [ollamaModel, setOllamaModel] = useState(cachedModel);
  const ollamaModelRef = useRef(cachedModel);

  const [stepStatus, setStepStatus] = useState(() =>
    BASE_STEPS.map(s =>
      (!isImageOrPDF && (s.id === "ocr" || s.id === "translate")) ? STATUS.SKIP : STATUS.PENDING
    )
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress]       = useState(0);
  const [elapsed, setElapsed]         = useState(0);
  const [apiDone, setApiDone]         = useState(false);
  const [apiError, setApiError]       = useState(null);
  const apiResult = useRef(null);

  // Fetch actual model name — update ref (for animation) + state (for render)
  useEffect(() => {
    fetch("/api/config")
      .then(r => r.json())
      .then(d => {
        if (d.ollama_model) {
          ollamaModelRef.current = d.ollama_model;
          localStorage.setItem("ollama_model", d.ollama_model);
          setOllamaModel(d.ollama_model);
        }
      })
      .catch(() => {});
  }, []);

  const PIPELINE_STEPS = BASE_STEPS.map(s => {
    if (s.id === "llm")       return { ...s, desc: `Ollama (${ollamaModel}) generating actionable recommendations` };
    if (s.id === "translate") return { ...s, desc: `Auto-detecting language · Translating to English via Ollama (${ollamaModel})` };
    return s;
  });

  // ── upload + poll ──────────────────────────────────────────────────────
  useEffect(() => {
    let stopped = false;
    let pollTimer = null;

    async function run() {
      try {
        const { job_id } = await startUpload(file);
        if (stopped) return;

        pollTimer = setInterval(async () => {
          try {
            const job = await pollJob(job_id);
            if (job.status === "done") {
              clearInterval(pollTimer);
              apiResult.current = job.result;
              setApiDone(true);
            } else if (job.status === "error") {
              clearInterval(pollTimer);
              setApiError(job.error || "Processing failed");
            }
          } catch {
            // transient poll error — keep retrying
          }
        }, POLL_INTERVAL_MS);
      } catch (e) {
        if (!stopped) setApiError(e?.response?.data?.detail || "Upload failed");
      }
    }

    run();
    return () => { stopped = true; if (pollTimer) clearInterval(pollTimer); };
  }, []);

  // ── animation ──────────────────────────────────────────────────────────
  // Uses a local closure variable (not a ref) so React StrictMode's double
  // invocation in dev doesn't freeze the animation at step 0.
  useEffect(() => {
    let stopped = false;
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);

    async function runAnimation() {
      const statuses = PIPELINE_STEPS.map(() => STATUS.PENDING);
      if (!stopped) setStepStatus([...statuses]);

      for (let i = 0; i < PIPELINE_STEPS.length; i++) {
        if (stopped) return;
        if ((PIPELINE_STEPS[i].id === "ocr" || PIPELINE_STEPS[i].id === "translate") && !isImageOrPDF) {
          statuses[i] = STATUS.SKIP;
          setStepStatus([...statuses]);
          continue;
        }
        statuses[i] = STATUS.ACTIVE;
        setStepStatus([...statuses]);
        setCurrentStep(i);
        setProgress(Math.round((i / PIPELINE_STEPS.length) * 100));
        await sleep(PIPELINE_STEPS[i].duration);
        if (stopped) return;
        statuses[i] = STATUS.DONE;
        setStepStatus([...statuses]);
      }
      if (!stopped) setProgress(100);
    }

    runAnimation();
    return () => { stopped = true; clearInterval(timer); };
  }, []);

  // ── resolve when both animation + API are done ─────────────────────────
  useEffect(() => {
    if (progress === 100 && apiDone && apiResult.current) {
      setTimeout(() => onDone(apiResult.current), 500);
    }
    if (progress === 100 && apiError) {
      onError(apiError);
    }
  }, [progress, apiDone, apiError]);

  const activeStep = PIPELINE_STEPS[currentStep];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>⚙️</div>
          <div>
            <h2 style={styles.title}>OCR + AI Processing Workflow</h2>
            <p style={styles.subtitle}>Analyzing: <strong>{file?.name}</strong></p>
          </div>
        </div>

        {/* Progress bar */}
        <div style={styles.progressWrap}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <div style={styles.progressMeta}>
            <span style={styles.progressPct}>{progress}% complete</span>
            <span style={styles.progressTime}>⏱ {elapsed}s elapsed</span>
          </div>
        </div>

        {/* Current step banner */}
        {progress < 100 && activeStep && (
          <div style={styles.currentStepBanner}>
            <span style={styles.pulsingDot} />
            <span style={{ fontWeight: 600 }}>{activeStep.label}</span>
            <span style={{ color: "#6b7280", marginLeft: 8 }}>— {activeStep.desc}</span>
          </div>
        )}

        {/* Pipeline steps */}
        <div style={styles.pipeline}>
          {PIPELINE_STEPS.map((step, i) => {
            const status  = stepStatus[i];
            if (status === STATUS.SKIP) return null;
            const isActive = status === STATUS.ACTIVE;
            const isDone   = status === STATUS.DONE;

            return (
              <div key={step.id} style={styles.stepRow}>
                {i > 0 && (
                  <div style={{ ...styles.connector, background: isDone || isActive ? "#1a56db" : "#e5e7eb" }} />
                )}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{
                    ...styles.stepIcon,
                    background: isDone ? "#dcfce7" : isActive ? "#eff6ff" : "#f3f4f6",
                    border: `2px solid ${isDone ? "#057a55" : isActive ? "#1a56db" : "#d1d5db"}`,
                    boxShadow: isActive ? "0 0 0 4px #bfdbfe" : "none",
                  }}>
                    {isDone ? "✓" : isActive ? (
                      <span style={styles.spinner}>◌</span>
                    ) : step.icon}
                  </div>

                  <div style={styles.stepContent}>
                    <div style={{ ...styles.stepLabel, color: isDone ? "#057a55" : isActive ? "#1a56db" : "#9ca3af" }}>
                      {step.label}
                      {isActive && <span style={styles.processingTag}>Processing...</span>}
                      {isDone   && <span style={styles.doneTag}>Done</span>}
                    </div>
                    <div style={styles.stepDesc}>{step.desc}</div>

                    {step.id === "ocr" && isActive && (
                      <div style={styles.subWorkflow}>
                        {["Load OCR Model", "Detect Text Regions", "Recognize Characters", "Output Raw Text"].map(s => (
                          <div key={s} style={styles.subStep}><span style={{ color: "#7c3aed", marginRight: 4 }}>›</span>{s}</div>
                        ))}
                      </div>
                    )}
                    {step.id === "translate" && isActive && (
                      <div style={styles.subWorkflow}>
                        {["Detect language (English / Gujarati / Hindi / Mixed)", "Skip if already English", "Translate non-English text via Ollama", "Output: Clean English text"].map(s => (
                          <div key={s} style={styles.subStep}><span style={{ color: "#057a55", marginRight: 4 }}>›</span>{s}</div>
                        ))}
                      </div>
                    )}
                    {step.id === "ml" && isActive && (
                      <div style={styles.subWorkflow}>
                        {["Malnutrition Risk Model", "Anemia Risk Model", "Growth Risk Model"].map(s => (
                          <div key={s} style={styles.subStep}><span style={{ color: "#1a56db", marginRight: 4 }}>›</span>{s}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Waiting for server (animation done, API still running) */}
        {progress === 100 && !apiDone && !apiError && (
          <div style={styles.waitingBanner}>
            <span style={styles.pulsingDot} />
            Server still processing large file — polling every 2s…
          </div>
        )}

        {apiError && (
          <div style={styles.errorBanner}>⚠️ {apiError}</div>
        )}
      </div>
    </div>
  );
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

const styles = {
  container: { display: "flex", justifyContent: "center" },
  card: { background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.08)", padding: 36, width: "100%", maxWidth: 700 },
  header: { display: "flex", alignItems: "center", gap: 16, marginBottom: 24 },
  headerIcon: { fontSize: 40 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" },
  subtitle: { margin: "4px 0 0", color: "#6b7280", fontSize: 14 },
  progressWrap: { marginBottom: 20 },
  progressBar: { height: 10, background: "#e5e7eb", borderRadius: 10, overflow: "hidden", marginBottom: 8 },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #1a56db, #7c3aed)", borderRadius: 10, transition: "width 0.4s ease" },
  progressMeta: { display: "flex", justifyContent: "space-between" },
  progressPct: { fontSize: 13, fontWeight: 600, color: "#1a56db" },
  progressTime: { fontSize: 13, color: "#9ca3af" },
  currentStepBanner: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#eff6ff", border: "1px solid #bfdbfe",
    borderRadius: 8, padding: "10px 14px", marginBottom: 24, fontSize: 14,
  },
  pulsingDot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#1a56db", animation: "pulse 1.5s infinite" },
  pipeline: { display: "flex", flexDirection: "column", gap: 0 },
  stepRow: { position: "relative" },
  connector: { width: 2, height: 20, marginLeft: 22, background: "#e5e7eb", transition: "background 0.3s" },
  stepIcon: { width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0, transition: "all 0.3s" },
  spinner: { animation: "spin 1s linear infinite", display: "inline-block", fontSize: 20 },
  stepContent: { paddingBottom: 4, flex: 1 },
  stepLabel: { fontWeight: 600, fontSize: 15, display: "flex", alignItems: "center", gap: 8 },
  processingTag: { fontSize: 11, background: "#eff6ff", color: "#1a56db", border: "1px solid #bfdbfe", borderRadius: 20, padding: "1px 8px" },
  doneTag: { fontSize: 11, background: "#f0fdf4", color: "#057a55", border: "1px solid #bbf7d0", borderRadius: 20, padding: "1px 8px" },
  stepDesc: { fontSize: 13, color: "#9ca3af", marginTop: 2 },
  subWorkflow: { marginTop: 8, padding: "8px 12px", background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 6 },
  subStep: { fontSize: 12, color: "#6b7280", padding: "2px 0" },
  waitingBanner: { display: "flex", alignItems: "center", gap: 8, background: "#fefce8", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginTop: 20, fontSize: 14, color: "#92400e" },
  errorBanner: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", marginTop: 20, fontSize: 14, color: "#991b1b" },
};
